import { maskedRegionProps } from "@koaform/analytics";
import type { FormDoc } from "@koaform/form-schema";
import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { Builder } from "./builder/Builder.js";
import { api, readWip, type Draft } from "./lib/api.js";
import { Admin } from "./pages/Admin.js";
import { Home } from "./pages/Home.js";
import { Responses } from "./responses/Responses.js";

/**
 * /new — but refresh-proof.
 *
 * Once autosave creates a draft, Builder writes ?draft=<id> into the URL, so
 * a refresh lands back here with the id and resumes the same draft instead of
 * a blank editor. The localStorage mirror covers what the URL cannot: edits
 * from the debounce window and content typed before the first save existed.
 *
 * Precedence: a draft id (URL, then mirror) beats ?template — after the first
 * save both params are present, and re-seeding the template would wipe edits.
 */
const NewRoute = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const draftParam = params.get("draft");
  const template = params.get("template");
  const [state, setState] = useState<
    { phase: "loading" } | { phase: "ready"; draft?: Draft; wipDoc?: FormDoc }
  >({ phase: "loading" });

  useEffect(() => {
    setState({ phase: "loading" });
    const wip = readWip();
    const id = draftParam ?? wip?.draftId ?? null;

    if (id && !draftParam) {
      // Put the id in the URL so the next refresh needs no localStorage.
      navigate(`/new?draft=${id}`, { replace: true });
      return; // The param change re-runs this effect, which then loads.
    }
    if (!id) {
      setState({
        phase: "ready",
        wipDoc: template ? undefined : wip?.doc,
      });
      return;
    }
    api
      .getDraft(id)
      .then((draft) =>
        setState({
          phase: "ready",
          draft,
          wipDoc: wip && wip.draftId === id ? wip.doc : undefined,
        }),
      )
      // Gone or not ours — keep at least the mirrored content.
      .catch(() => setState({ phase: "ready", wipDoc: wip?.doc }));
  }, [draftParam, template, navigate]);

  if (state.phase === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center bg-paper">
        <p className="text-sm text-faint">Loading…</p>
      </div>
    );
  }
  return <Builder existing={state.draft} wipDoc={state.wipDoc} />;
};

const ResponsesRoute = () => {
  const { formId } = useParams<{ formId: string }>();
  return formId ? <Responses formId={formId} /> : <Navigate to="/" replace />;
};

/**
 * Loads an existing form into the builder — the dashboard's Edit button.
 * Until this route existed, the builder could only ever create new drafts.
 */
const EditRoute = () => {
  const { formId } = useParams<{ formId: string }>();
  const [state, setState] = useState<
    | { phase: "loading" }
    | { phase: "missing" }
    | { phase: "ready"; draft: Draft }
  >({ phase: "loading" });

  useEffect(() => {
    if (!formId) return;
    api
      .getDraft(formId)
      .then((draft) => setState({ phase: "ready", draft }))
      .catch(() => setState({ phase: "missing" }));
  }, [formId]);

  if (state.phase === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center bg-paper">
        <p className="text-sm text-faint">Loading…</p>
      </div>
    );
  }
  if (state.phase === "missing") {
    return (
      <div className="grid min-h-dvh place-items-center bg-paper">
        <div className="text-center">
          <p className="m-0 mb-3 text-[19px] font-bold">That form is gone</p>
          <a href="/" className="text-sm font-semibold">
            Back to your forms
          </a>
        </div>
      </div>
    );
  }
  return <Builder existing={state.draft} />;
};

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/new" element={<NewRoute />} />
        <Route path="/forms/:formId/edit" element={<EditRoute />} />
        <Route path="/forms/:formId/responses" element={<ResponsesRoute />} />
        {/* The API 404s this for non-admins; the route is not a secret. */}
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * Wrap anything showing respondent-submitted answers.
 *
 * Clarity masking is relaxed across the app, which is right for the operator's
 * own UI — but the responses table and single-response view are full of things
 * strangers typed, and those must not land in session replays.
 */
export function MaskedRegion({ children }: { children: React.ReactNode }) {
  return <div {...maskedRegionProps}>{children}</div>;
}
